import { useQueryClient } from '@mint-lib/query-client';
import * as DataIngestion from '@mint-sdk/api-data-ingestion';

import * as API from '../../api/index.js';
import type {
  DataFilePreview,
  GranularityLevel,
  HeadersMappingQueryResult,
  SystemMetric,
} from '../../api/types.js';
import {
  useImportHubV2,
  useSimplifiedHeadersMetaFF,
} from '../../hooks/ff/index.js';
import { useEnhancedMutation, useTypedQuery } from '../../hooks/index.js';
import { useDataFilePreviewQueryOptions } from './preview.js';

// Shared key prefix of all headers-meta (system and custom metrics) queries
const METRICS_LIST_QUERY_KEY = ['metricsList'];

export const useHeadersMapping = (
  dataFileId: string,
  options?: { enabled?: boolean; throwOnError?: boolean },
) => {
  const queryOptions = DataIngestion.DataFile.getHeadersMappingQueryOptions({
    urlParams: { id: dataFileId },
  });

  return useTypedQuery({
    ...queryOptions,
    placeholderData: {
      items: [],
      additional: {
        canBeMapped: false,
        maxCount: 0,
      },
    },
    staleTime: 30 * 1000,
    ...options,
  });
};

export function useDataFileMetricsQueryOptions({
  granularityLevel,
  dataFileId,
}: {
  granularityLevel: GranularityLevel;
  dataFileId: string;
}) {
  const isImportHubV2 = useImportHubV2();
  const isSimplifiedHeadersMeta = useSimplifiedHeadersMetaFF();

  const params = { granularityLevelType: granularityLevel };

  const v1QueryOptions = isSimplifiedHeadersMeta
    ? DataIngestion.SystemMetrics.getSimplifiedSystemMetricsQueryOptions({
        params,
      })
    : DataIngestion.SystemMetrics.getSystemMetricsQueryOptions({ params });

  const v2QueryOptions = isSimplifiedHeadersMeta
    ? DataIngestion.SystemMetrics.getSimplifiedDataFileSystemMetricsQueryOptions(
        { urlParams: { id: dataFileId }, params },
      )
    : DataIngestion.SystemMetrics.getDataFileSystemMetricsQueryOptions({
        urlParams: { id: dataFileId },
        params,
      });

  const queryOptions = (
    isImportHubV2 ? v2QueryOptions : v1QueryOptions
  ) as ReturnType<
    typeof DataIngestion.SystemMetrics.getSystemMetricsQueryOptions
  >;

  return queryOptions;
}

export function useDataFileMetrics(
  {
    dataFileId,
    granularityLevel,
  }: { dataFileId: string; granularityLevel: GranularityLevel },
  options?: { enabled?: boolean; throwOnError?: boolean },
) {
  const queryOptions = useDataFileMetricsQueryOptions({
    dataFileId,
    granularityLevel,
  });

  return useTypedQuery({
    ...queryOptions,
    select(data) {
      return {
        systemMetrics: data.items,
        customMetrics: data.additional.customMetrics,
        maxMetricsCount: data.additional.maxCount ?? Infinity,
      };
    },
    placeholderData: {
      items: [],
      additional: { customMetrics: [], maxCount: Infinity },
    },
    staleTime: 30 * 1000,
    ...options,
  });
}

export const useMapTableHeader = ({
  dataFileId,
  granularityLevel,
}: {
  dataFileId: string;
  granularityLevel: GranularityLevel;
}) => {
  const queryClient = useQueryClient();

  const headersMappingQueryKey =
    DataIngestion.DataFile.getHeadersMappingQueryOptions({
      urlParams: { id: dataFileId },
    }).queryKey;

  const { queryKey: systemMetricsQueryKey } = useDataFileMetricsQueryOptions({
    dataFileId,
    granularityLevel,
  });

  return useEnhancedMutation({
    mutationFn: ({
      id,
      payload,
      isDiscard,
    }: {
      id: string;
      payload: { name: string | null; column_position: number };
      isDiscard: boolean;
    }) => {
      const updateFunction = isDiscard ? API.discardHeader : API.updateHeader;
      return updateFunction({ id, payload });
    },

    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: headersMappingQueryKey,
      });

      const previousValue = queryClient.getQueryData<HeadersMappingQueryResult>(
        headersMappingQueryKey,
      );

      const systemMetricsValue =
        queryClient.getQueryData<DataIngestion.SystemMetrics.MetricsQueryResult>(
          systemMetricsQueryKey,
        );
      if (previousValue && systemMetricsValue) {
        const dimensionsByName = systemMetricsValue.items.reduce(
          (acc, metric) => {
            if (metric.headerType === 'dimension') {
              acc[metric.name] = metric;
            }
            return acc;
          },
          {} as { [key: SystemMetric['name']]: SystemMetric },
        );

        queryClient.setQueryData<HeadersMappingQueryResult>(
          headersMappingQueryKey,
          () => {
            const {
              items,
              additional: { maxCount },
            } = previousValue;
            const updatedItems = items.map((header) => {
              return header.position === variables.payload.column_position
                ? {
                    ...header,
                    mappedHeader: variables.isDiscard
                      ? null
                      : variables.payload.name,
                  }
                : header;
            });

            const updatedMappedMetricsCount = updatedItems.reduce(
              (acc, header) => {
                return header.mappedHeader &&
                  !dimensionsByName[header.mappedHeader]
                  ? acc + 1
                  : acc;
              },
              0,
            );

            return {
              ...previousValue,
              items: updatedItems,
              additional: {
                ...previousValue.additional,
                canBeMapped: updatedMappedMetricsCount < maxCount,
              },
            };
          },
        );
      }

      return { previousValue };
    },

    onError: (err, vars, context) => {
      queryClient.setQueryData(headersMappingQueryKey, context?.previousValue);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: headersMappingQueryKey });
    },
    meta: {
      getErrorsMap(t) {
        return {
          default: t('Failed to map header'),
        };
      },
    },
  });
};

export const useSetGranularityLevel = (dataFileId: string) => {
  const queryClient = useQueryClient();

  const { queryKey: previewQueryKey } = useDataFilePreviewQueryOptions({
    urlParams: { id: dataFileId },
  });

  const { queryKey: headersMappingQueryKey } =
    DataIngestion.DataFile.getHeadersMappingQueryOptions({
      urlParams: { id: dataFileId },
    });

  return useEnhancedMutation({
    mutationFn: (payload: { granularityLevel: GranularityLevel }) =>
      API.setGranularityLevel(dataFileId, payload),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: previewQueryKey });

      const previousValue =
        queryClient.getQueryData<DataFilePreview>(previewQueryKey);

      if (previousValue) {
        const shouldClearPlatform =
          vars.granularityLevel !== 'pricing_item' &&
          previousValue.context?.platformName;

        queryClient.setQueryData<DataFilePreview>(previewQueryKey, {
          ...previousValue,
          granularityLevel: vars.granularityLevel,
          ...(shouldClearPlatform && {
            context: {
              ...previousValue.context,
              platformName: null,
            },
          }),
        });
      }

      return { previousValue };
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: headersMappingQueryKey });
    },
    onError: (err, vars, context) => {
      queryClient.setQueryData(previewQueryKey, context?.previousValue);
    },
    onSettled() {
      void queryClient.invalidateQueries({
        queryKey: previewQueryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: METRICS_LIST_QUERY_KEY,
      });
    },
    meta: {
      getErrorsMap(t) {
        return {
          default: t(
            'Failed to change the attribution level of your performance data',
          ),
        };
      },
    },
  });
};
